import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Noop } from 'types/common.interface';

import { IValidationResult } from 'models/i-form-validation.interface';
import { IInputValue } from 'models/i-input-value.interface';
import { IProfile } from 'models/profile/i-profile.interface';
import { IState } from 'models/reducers/i-state.interface';

import { REGEX_PATTERN } from 'constants/defaults.constant';

import { addErrorMessageAction } from 'actions/global-actions';
import { updateProfile } from 'actions/profile.action';

import Button from 'components/buttons/Button';
import Form from 'components/form/Form';
import InputValidation from 'components/inputValidation/InputValidation';
import ModalPortal from 'components/modal/ModalPortal';

import { useEnv } from 'hooks/useEnv';

import ProfileAvatarReferralClean from 'assets/profile/icons/profile-avatar-referral-clean.svg';

interface IReferralProfileEditModal {
  onClose: Noop;
}

const avatarErrorMessage = 'Avatar size must not exceed 2 mb';

export const ReferralProfileEditModal = ({ onClose }: IReferralProfileEditModal) => {
  const dispatch = useDispatch();
  const { disableAvatarUpload } = useEnv();
  const imgRef = useRef<HTMLImageElement>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);
  const { first_name, email, avatar, telegram_username } = useSelector(
    ({ profile }: IState): IProfile => profile.profile!
  );
  const [userName, setUserName] = useState<string>(first_name);
  const [userEmail, setUserEmail] = useState<string>(email);
  const [userTelegram, setUserTelegram] = useState<string>(telegram_username || '');
  const [userAvatar, setUserAvatar] = useState<File | null>(null);
  const [avatarToLarge, setAvatarToLarge] = useState<boolean>(false);
  const didUserNameChange = first_name !== userName;
  const didEmailChange = email !== userEmail;
  const didTelegramChange = (telegram_username || '') !== userTelegram;
  const didAvatarChanged = !!userAvatar;
  const didAnyProfileDataChanged = didUserNameChange || didEmailChange || didTelegramChange || didAvatarChanged;
  const regEmail = REGEX_PATTERN.email;

  const updateProfileData = (): void => {
    if (!didAnyProfileDataChanged) {
      return;
    }

    const formData = new FormData();

    if (didUserNameChange) {
      formData.set('first_name', userName);
    }

    if (didEmailChange) {
      formData.set('email', userEmail);
    }

    if (didTelegramChange) {
      formData.set('telegram_username', userTelegram);
    }

    if (didAvatarChanged) {
      formData.set('avatar', userAvatar);
    }

    dispatch(updateProfile(formData));
    setUserAvatar(null);
    onClose();
  };

  const onAvatarChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files;

    if (!files) {
      return;
    }

    const uploadedImage = files[0];

    if (uploadedImage.size > 2_097_152) {
      setAvatarToLarge(true);
      dispatch(addErrorMessageAction(avatarErrorMessage));
      return;
    }

    imgRef.current!.src = URL.createObjectURL(uploadedImage);
    setAvatarToLarge(false);
    setUserAvatar(uploadedImage);
  };

  const maxLengthValidation = (inputValue: IInputValue): IValidationResult => {
    const value = inputValue as string;

    return value.length < 30
      ? { valid: true }
      : { valid: false, message: 'Your username should not have more then 30 characters' };
  };

  const minLengthValidation = (inputValue: IInputValue): IValidationResult => {
    const value = inputValue as string;

    return value.length >= 1
      ? { valid: true }
      : { valid: false, message: 'Your username should have at least 1 character' };
  };

  const emailPattern = (inputValue: IInputValue): IValidationResult => {
    const value = inputValue as string;

    return regEmail.test(value)
      ? { valid: true }
      : { valid: false, message: 'Your email should have at least 1 character' };
  };

  const avatarSizeValidation = (): IValidationResult => {
    return !avatarToLarge ? { valid: true } : { valid: false, message: 'Avatar size must not exceed 2 mb' };
  };

  return (
    <ModalPortal
      title="Edit Profile"
      titleClassName="!tw-capitalize tw-text-[40px] tw-font-jost"
      modalContentClassName="tw-p-[20px] tw-bg-[#111213] tw-border-[1px] tw-border-[#2D5EFF] tw-border-solid tw-w-[350px] lg:tw-w-[596px]"
      onClose={onClose}
    >
      <Form className="tw-flex tw-flex-col tw-gap-[20px] tw-font-jost" onSubmit={() => updateProfileData()}>
        <div className="tw-flex tw-flex-col tw-gap-[10px]">
          <div className="tw-flex tw-h-[140px] tw-w-[140px] tw-flex-col tw-items-center tw-justify-center tw-gap-[20px] tw-self-center tw-rounded-full tw-border-2 tw-border-[#323232] tw-bg-[#0E1010] tw-p-[20px] tw-shadow-[inset_0_0_25.7px_0_rgba(140,140,140,0.30)]">
            <img
              className={twMerge(
                'tw-h-[105px] tw-w-[105px] tw-rounded-full tw-border-4 tw-border-white',
                disableAvatarUpload ? 'tw-cursor-default' : 'tw-cursor-pointer'
              )}
              alt="user avatar"
              id="uploadImage"
              ref={imgRef}
              src={avatar || ProfileAvatarReferralClean}
              onClick={() => !disableAvatarUpload && imgInputRef.current?.click()}
            />
          </div>

          <input
            ref={imgInputRef}
            className="tw-absolute tw-h-0 tw-w-0 tw-opacity-0"
            type="file"
            disabled={disableAvatarUpload}
            onChange={onAvatarChange}
            accept="image/png, imgae/jpg, image/jpeg"
          />
          <InputValidation value={avatarToLarge} validator={avatarSizeValidation}></InputValidation>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[10px]">
          <label className="tw-text-center tw-text-[20px] tw-font-bold tw-text-white">Username</label>
          <div className="tw-relative tw-flex tw-items-center">
            <span className="tw-absolute tw-left-[20px] tw-text-[20px] tw-text-white">@</span>
            <input
              className="tw-flex tw-w-full tw-items-center tw-gap-[10px] tw-rounded-[10px] tw-border-0 tw-bg-[#314272] tw-py-[10px] tw-pl-[40px] tw-pr-[20px] tw-text-[20px] tw-text-white placeholder:tw-text-[20px] placeholder:tw-text-[#2D5EFF]"
              type="text"
              value={userName}
              placeholder="Enter username"
              onChange={(event) => setUserName(event.target.value)}
            />
          </div>
          {[
            { key: 'maxLengthValidation', validator: maxLengthValidation },
            { key: 'minLengthValidation', validator: minLengthValidation },
          ].map((validation) => (
            <InputValidation key={validation.key} validator={validation.validator} value={userName} />
          ))}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[10px]">
          <label className="tw-text-center tw-text-[20px] tw-font-bold tw-text-white">E-mail</label>
          <div className="tw-relative tw-flex tw-items-center">
            <span className="tw-absolute tw-left-[20px] tw-text-[20px] tw-text-white">@</span>
            <input
              className="tw-flex tw-w-full tw-items-center tw-gap-[10px] tw-rounded-[10px] tw-border-0 tw-bg-[#314272] tw-py-[10px] tw-pl-[40px] tw-pr-[20px] tw-text-[20px] tw-text-white placeholder:tw-text-[20px] placeholder:tw-text-[#2D5EFF]"
              type="text"
              value={userEmail}
              placeholder="Enter your e-mail address"
              onChange={(event) => setUserEmail(event.target.value)}
            />
          </div>
          <InputValidation key="minLengthEmailPatternValidation" validator={emailPattern} value={userEmail} />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[10px]">
          <label className="tw-text-center tw-text-[20px] tw-font-bold tw-text-white">Telegram</label>
          <div className="tw-relative tw-flex tw-items-center">
            <span className="tw-absolute tw-left-[20px] tw-text-[20px] tw-text-white">@</span>
            <input
              className="tw-flex tw-w-full tw-items-center tw-gap-[10px] tw-rounded-[10px] tw-border-0 tw-bg-[#314272] tw-py-[10px] tw-pl-[40px] tw-pr-[20px] tw-text-[20px] tw-text-white placeholder:tw-text-[20px] placeholder:tw-text-[#2D5EFF]"
              type="text"
              value={userTelegram}
              placeholder="Enter telegram username"
              onChange={(event) => setUserTelegram(event.target.value)}
            />
          </div>
        </div>

        <Button
          type="submit"
          className="tw-mt-[10px] tw-flex tw-h-[56px] tw-items-center tw-justify-center tw-gap-[10px] tw-self-center tw-rounded-[50px] tw-border-0 tw-bg-gamefieldProgressGradient tw-px-[20px] tw-py-[10px] tw-text-[20px] tw-font-semibold tw-text-white"
          text="Save Information"
          color="referral-cta"
          size="medium"
          contentClassName="tw-capitalize"
        />
      </Form>
    </ModalPortal>
  );
};
